// src/components/modules/TowerSvgDiagram.jsx
import React, { useMemo, useState, useCallback } from 'react';
import { Box, ToggleButtonGroup, ToggleButton, useTheme } from '@mui/material';

const T = 'transparent';

/* ── Lógica de Cores Traduzida do Java ── */

function getTemperatureColor(temperature) {
    if (temperature == null || isNaN(temperature)) return T;

    const minTemperature = 10;
    const maxTemperature = 80;

    const clampedTemp = Math.max(
        minTemperature,
        Math.min(maxTemperature, temperature)
    );
    const normalizedTemperature =
        (clampedTemp - minTemperature) / (maxTemperature - minTemperature);

    const colors = [
        [0, 0, 255], // Azul
        [255, 255, 255], // Branco
        [255, 255, 0], // Amarelo
        [255, 165, 0], // Laranja
        [255, 0, 0], // Vermelho
    ];

    const numIntervals = colors.length - 1;
    const interval = Math.floor(normalizedTemperature * numIntervals);
    const ratio = normalizedTemperature * numIntervals - interval;

    let r, g, b;
    if (interval < numIntervals) {
        r = Math.round(
            colors[interval][0] * (1 - ratio) + colors[interval + 1][0] * ratio
        );
        g = Math.round(
            colors[interval][1] * (1 - ratio) + colors[interval + 1][1] * ratio
        );
        b = Math.round(
            colors[interval][2] * (1 - ratio) + colors[interval + 1][2] * ratio
        );
    } else {
        r = colors[numIntervals][0];
        g = colors[numIntervals][1];
        b = colors[numIntervals][2];
    }

    r = Math.min(255, Math.max(0, r));
    g = Math.min(255, Math.max(0, g));
    b = Math.min(255, Math.max(0, b));

    return `rgba(${r}, ${g}, ${b}, 1)`;
}

// getSolutionColor removed — color now comes only from API (`sensorColors`) or fallback

// Cores dos LEDs UI
const LED_COLORS = {
    green: '#43A047',
    blue: '#1E88E5',
    amber: '#FFB300',
    purple: '#AB47BC',
    off: '#555555',
};

const SENSOR_KEYS = ['turbidimetry', 'nephelometry', 'fluorescence'];

/* Posições dos sensores agrupados no meio do Núcleo (Core vai de 65 a 175) */
const SENSOR_LEDS = [
    { cy: 88, btnY: 88, shortName: 'Turb.', label: 'T', color: '#FF5252' },
    { cy: 113, btnY: 113, shortName: 'Nef.', label: 'N', color: '#448AFF' },
    { cy: 138, btnY: 138, shortName: 'Fluor.', label: 'F', color: '#E040FB' },
];

/* Frequência ressonante — botão extra abaixo dos sensores ópticos */
const FREQ_LED = { cy: 163, btnY: 163, shortName: 'Freq.', color: '#26C6DA' };

/* Furos de ventilação frontais da carcaça externa — 5 anéis × 16 furos (CAD ref) */
const VENT_CENTER = { x: 147, y: 345 };
const VENT_CORE_R = 10; // raio do disco maciço central (cor da carcaça)
const VENT_COUNT = 16; // 16 furos por anel, pitch angular 22.5°
const VENT_RINGS = [
    { ringR: 20, holeR: 2.0 }, // anel 1
    { ringR: 25, holeR: 3.0 }, // anel 2
    { ringR: 32, holeR: 4.0 }, // anel 3
    { ringR: 40, holeR: 5 }, // anel 4
    { ringR: 51, holeR: 7 }, // anel 5
];
const VENT_HALF_PITCH = Math.PI / VENT_COUNT;
const VENT_HOLES = [];
VENT_RINGS.forEach((ring, ringIdx) => {
    const offset = ringIdx % 2 === 1 ? VENT_HALF_PITCH : 0;
    for (let j = 0; j < VENT_COUNT; j++) {
        const theta = offset + (2 * Math.PI * j) / VENT_COUNT;
        VENT_HOLES.push({
            x: VENT_CENTER.x + ring.ringR * Math.cos(theta),
            y: VENT_CENTER.y + ring.ringR * Math.sin(theta),
            r: ring.holeR,
        });
    }
});

/* ── COMPONENTE PRINCIPAL ────────────────────────────── */

export default function TowerSvgDiagram({
    temps,
    sensorColors = [],
    spectral,
    freq,
    isOnline,
    status,
    hasProtocol = true,
}) {
    // O DESENHO em si representa a carcaça física do equipamento, que é
    // branca — não deve escurecer no dark mode do app (isDark fixo em false).
    // Os controles ao redor (abas Externo/Amostra/etc.) são UI comum e devem
    // continuar seguindo o tema normalmente (isDarkUI).
    const theme = useTheme();
    const isDarkUI = theme.palette.mode === 'dark';
    const isDark = false;
    const txtColor = '#333333';

    // ViewModes: 3 (Externo), 0 (Amostra), 1 (Interno/Core), 2 (Dissipadores)
    const [viewMode, setViewMode] = useState(3);
    const [sensorIdx, setSensorIdx] = useState(0);

    const isBusy = status === 'busy';
    const isLocked = status === 'locked';
    const isAvailable = status === 'available';

    // Cor da amostra vinda da API (useSensorColor) — sem cálculo local, apenas fallback
    const apiColor = sensorColors[sensorIdx];
    const activeSensorData = spectral?.[SENSOR_KEYS[sensorIdx]];
    const tubeColorSample =
        apiColor?.status === 'ok' && apiColor?.color
            ? apiColor.color
            : 'transparent';
    const tubeOpacity = apiColor?.status === 'ok' ? apiColor.opacity : 1;

    // Obter cores de temperatura
    const tc = useMemo(
        () => ({
            core: getTemperatureColor(temps?.core),
            sample: getTemperatureColor(temps?.sample),
            ambient: getTemperatureColor(temps?.ambient),
            hsUp: getTemperatureColor(temps?.heatsinkUpper),
            hsLow: getTemperatureColor(temps?.heatsinkLower),
            hxSamp: getTemperatureColor(temps?.sampleHeatExchanger), // Trocador de calor
            stir: getTemperatureColor(temps?.magneticStirrer),
        }),
        [temps]
    );

    // Lógica dos LEDs Frontais
    const ledCloud = isOnline ? LED_COLORS.green : LED_COLORS.amber;
    let ledStatus = LED_COLORS.off;
    if (isAvailable) ledStatus = LED_COLORS.green;
    if (isBusy && hasProtocol) ledStatus = LED_COLORS.blue;
    if (isBusy && !hasProtocol) ledStatus = LED_COLORS.purple;
    if (isLocked) ledStatus = LED_COLORS.amber;
    const ledPower =
        isOnline || isAvailable || isBusy ? LED_COLORS.green : LED_COLORS.off;

    // Gerenciador de Opacidade Clean para as 4 Abas
    const z = useMemo(() => {
        const off = { op: 0 };
        if (!isOnline && !isBusy && !isAvailable) {
            return {
                shellFront: { op: 1 },
                internalBg: off,
                hs: off,
                core: off,
                pcb: off,
                tube: off,
                tubeGlass: off,
            };
        }
        switch (viewMode) {
            case 0: // AMOSTRA
                return {
                    shellFront: { op: 0 },
                    internalBg: { op: 1 },
                    hs: { op: 0.05 },
                    core: { op: 0.1 },
                    pcb: { op: 0.15 },
                    tube: { fill: tubeColorSample, op: tubeOpacity },
                    tubeGlass: { op: 0.6 },
                };
            case 1: // TEMP INTERNA
                return {
                    shellFront: { op: 0 },
                    internalBg: { op: 1 },
                    hs: { op: 0.15 },
                    core: { op: 0.95 },
                    pcb: { op: 0.7 },
                    tube: { fill: tc.sample, op: 0.9 },
                    tubeGlass: { op: 0.2 },
                };
            case 2: // DISSIPADORES
                return {
                    shellFront: { op: 0 },
                    internalBg: { op: 1 },
                    hs: { op: 0.95 },
                    core: { op: 0.1 },
                    pcb: { op: 0.3 },
                    tube: { fill: tc.sample, op: 0.1 },
                    tubeGlass: { op: 0.05 },
                };
            case 3: // EXTERNO
            default:
                return {
                    shellFront: { op: 1 },
                    internalBg: off,
                    hs: off,
                    core: off,
                    pcb: off,
                    tube: off,
                    tubeGlass: off,
                };
        }
    }, [
        viewMode,
        isOnline,
        isBusy,
        isAvailable,
        tc,
        tubeColorSample,
        tubeOpacity,
    ]);

    const handleView = useCallback((_e, v) => {
        if (v != null) setViewMode(v);
    }, []);
    const tubeFill = z.tube.fill || T;
    const tubeOp = z.tube.op ?? 0; // opacidade derivada de Y (xyY)
    const showGlass = z.tubeGlass.op > 0;

    return (
        <Box sx={{ width: '100%', maxWidth: 360, mx: 'auto', p: 1 }}>
            {/* Controles de Visão com 4 Abas na ordem lógica */}
            <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={handleView}
                size="small"
                fullWidth
                sx={{
                    mb: 3,
                    backgroundColor: isDarkUI ? '#161B22' : '#f5f5f5',
                    borderRadius: 2,
                    '& .MuiToggleButton-root': {
                        textTransform: 'none',
                        fontSize: '0.65rem',
                        fontWeight: 'bold',
                        px: 0.5,
                    },
                }}
            >
                <ToggleButton value={3}>EXTERNO</ToggleButton>
                <ToggleButton value={0}>AMOSTRA</ToggleButton>
                <ToggleButton value={1}>TEMP. INTERNA</ToggleButton>
                <ToggleButton value={2}>DISSIPADORES</ToggleButton>
            </ToggleButtonGroup>

            {/* SVG Principal */}
            <Box
                sx={{
                    '& svg': {
                        width: '100%',
                        height: 'auto',
                        display: 'block',
                    },
                }}
            >
                <svg viewBox="0 0 294 465" xmlns="http://www.w3.org/2000/svg">
                    <defs>
                        <filter
                            id="shadow-led"
                            x="-50%"
                            y="-50%"
                            width="200%"
                            height="200%"
                        >
                            <feGaussianBlur stdDeviation="3" result="blur" />
                            <feMerge>
                                <feMergeNode in="blur" />
                                <feMergeNode in="SourceGraphic" />
                            </feMerge>
                        </filter>

                        {/* Tubo de Ensaio — stopOpacity modulada por Y (xyY) */}
                        <linearGradient
                            id="dtb"
                            x1="129.066"
                            y1="0"
                            x2="166.473"
                            y2="0"
                            gradientUnits="userSpaceOnUse"
                        >
                            <stop
                                offset="0"
                                stopColor={tubeFill}
                                stopOpacity={0.95 * tubeOp}
                            />
                            <stop
                                offset=".3"
                                stopColor={tubeFill}
                                stopOpacity={0.7 * tubeOp}
                            />
                            <stop
                                offset=".7"
                                stopColor={tubeFill}
                                stopOpacity={0.8 * tubeOp}
                            />
                            <stop
                                offset="1"
                                stopColor={tubeFill}
                                stopOpacity={1.0 * tubeOp}
                            />
                        </linearGradient>
                        <linearGradient
                            id="dgl"
                            x1="129.066"
                            y1="0"
                            x2="166.473"
                            y2="0"
                            gradientUnits="userSpaceOnUse"
                        >
                            <stop
                                offset="0"
                                stopColor="#ffffff"
                                stopOpacity={
                                    showGlass ? z.tubeGlass.op * 0.7 : '0'
                                }
                            />
                            <stop
                                offset=".2"
                                stopColor="#ffffff"
                                stopOpacity={
                                    showGlass ? z.tubeGlass.op * 0.9 : '0'
                                }
                            />
                            <stop
                                offset=".35"
                                stopColor="#ffffff"
                                stopOpacity="0"
                            />
                            <stop
                                offset=".8"
                                stopColor="#ffffff"
                                stopOpacity="0"
                            />
                            <stop
                                offset="1"
                                stopColor="#ffffff"
                                stopOpacity={
                                    showGlass ? z.tubeGlass.op * 0.5 : '0'
                                }
                            />
                        </linearGradient>
                    </defs>

                    {/* =========================================
                        CHÃO E PÉS (Fixo)
                        ========================================= */}
                    <g>
                        <rect
                            x="0"
                            y="443"
                            width="294"
                            height="2"
                            fill={isDark ? '#444' : '#ccc'}
                        />
                        <rect
                            x="0"
                            y="445"
                            width="294"
                            height="20"
                            fill={isDark ? '#1a1a1a' : '#f5f5f5'}
                        />

                        <path
                            d="M70,440 h12 v2 a1,1 0 0,1 -12,0 z"
                            fill="#111"
                        />
                        <path
                            d="M212,440 h12 v2 a1,1 0 0,1 -12,0 z"
                            fill="#111"
                        />

                        <rect
                            x="47"
                            y="35"
                            width="200"
                            height="25"
                            fill="#111"
                        />
                        <path d="M117,35 l10,-15 h40 l10,15 z" fill="#111" />
                        <rect
                            x="144"
                            y="0"
                            width="6"
                            height="30"
                            rx="3"
                            fill="#111"
                        />
                        <line
                            x1="47"
                            y1="60"
                            x2="247"
                            y2="60"
                            stroke="#000"
                            strokeWidth="2"
                        />
                    </g>

                    {/* =========================================
                        FUNDO INTERNO (Chassi Aberto)
                        ========================================= */}
                    <g opacity={z.internalBg.op}>
                        <rect
                            x="55"
                            y="60"
                            width="183"
                            height="380"
                            fill={isDark ? '#222' : '#dcdcdc'}
                        />
                    </g>

                    {/* =========================================
                        PCB (Placa Base) no "chão" do equipamento
                        ========================================= */}
                    <g
                        opacity={z.pcb.op}
                        style={{ transition: 'all 0.3s ease' }}
                    >
                        {/* Base da Placa */}
                        <rect
                            x="65"
                            y="415"
                            width="164"
                            height="15"
                            fill={isDark ? '#2E7D32' : '#43A047'}
                            rx="2"
                            stroke={isDark ? '#000' : '#2E7D32'}
                            strokeWidth="1"
                        />

                        {/* Conectores Brancos */}
                        <rect
                            x="75"
                            y="408"
                            width="14"
                            height="7"
                            fill={isDark ? '#ccc' : '#fff'}
                            rx="1"
                            stroke={isDark ? '#888' : '#ccc'}
                            strokeWidth="0.5"
                        />
                        <rect
                            x="100"
                            y="408"
                            width="24"
                            height="7"
                            fill={isDark ? '#ccc' : '#fff'}
                            rx="1"
                            stroke={isDark ? '#888' : '#ccc'}
                            strokeWidth="0.5"
                        />
                        <rect
                            x="135"
                            y="408"
                            width="18"
                            height="7"
                            fill={isDark ? '#ccc' : '#fff'}
                            rx="1"
                            stroke={isDark ? '#888' : '#ccc'}
                            strokeWidth="0.5"
                        />
                        <rect
                            x="170"
                            y="408"
                            width="24"
                            height="7"
                            fill={isDark ? '#ccc' : '#fff'}
                            rx="1"
                            stroke={isDark ? '#888' : '#ccc'}
                            strokeWidth="0.5"
                        />
                        <rect
                            x="205"
                            y="408"
                            width="14"
                            height="7"
                            fill={isDark ? '#ccc' : '#fff'}
                            rx="1"
                            stroke={isDark ? '#888' : '#ccc'}
                            strokeWidth="0.5"
                        />

                        {/* Detalhes de chips */}
                        <rect
                            x="80"
                            y="422"
                            width="8"
                            height="4"
                            fill="#111"
                            rx="0.5"
                        />
                        <rect
                            x="140"
                            y="420"
                            width="14"
                            height="6"
                            fill="#111"
                            rx="0.5"
                        />
                        <rect
                            x="180"
                            y="422"
                            width="8"
                            height="4"
                            fill="#111"
                            rx="0.5"
                        />
                        <line
                            x1="70"
                            y1="418"
                            x2="220"
                            y2="418"
                            stroke="rgba(255,255,255,0.2)"
                            strokeWidth="1"
                            strokeDasharray="10 5"
                        />
                    </g>

                    {/* =========================================
                        CAMADA PROFUNDA: NÚCLEO, AGITADOR E TROCADOR DE CALOR
                        ========================================= */}
                    <g
                        opacity={z.core.op}
                        style={{ transition: 'all 0.3s ease' }}
                    >
                        {/* 1. NÚCLEO (Core Temperature) */}
                        <rect
                            x="96.13362"
                            y="65"
                            width="101.46293"
                            height="110"
                            fill={tc.core}
                            stroke={isDark ? '#333' : '#888'}
                            strokeWidth="1"
                        />

                        {/* 2. AGITADOR MAGNÉTICO (Magnetic Stirrer) */}
                        <rect
                            x="96.26299"
                            y="175"
                            width="101.33371"
                            height="72"
                            fill={tc.stir}
                            stroke={isDark ? '#333' : '#888'}
                            strokeWidth="1"
                        />

                        {/* 3. TROCADOR DE CALOR (Sample Heat Exchanger) */}
                        <path
                            d="M128.22647,180.91524h-8.0008V237.4201a5,5,0,0,1-5,5H78.47682v10.57833a20,20,0,0,0,20,20h96.90606a20,20,0,0,0,20-20V242.4201H178.634a5,5,0,0,1-5-5V180.91524h-8.0008Z"
                            fill={tc.hxSamp}
                            stroke={isDark ? '#444' : '#888'}
                            strokeWidth="1"
                        />

                        {/* Labels do Modo Interno */}
                        {viewMode === 1 && (
                            <g>
                                {/* Label Core */}
                                <rect
                                    x="5"
                                    y="106"
                                    width="85"
                                    height="18"
                                    rx="4"
                                    fill={
                                        isDark
                                            ? 'rgba(0,0,0,0.8)'
                                            : 'rgba(255,255,255,0.9)'
                                    }
                                />
                                <text
                                    x="47"
                                    y="118"
                                    textAnchor="middle"
                                    fontSize="8"
                                    fill={txtColor}
                                    fontWeight="bold"
                                >
                                    Núcleo {temps?.core?.toFixed(1) ?? '—'}°C
                                </text>
                                <line
                                    x1="90"
                                    y1="115"
                                    x2="96.1"
                                    y2="115"
                                    stroke={txtColor}
                                    strokeWidth="1"
                                    strokeDasharray="2"
                                />

                                {/* Label Agitador */}
                                <rect
                                    x="5"
                                    y="201"
                                    width="85"
                                    height="18"
                                    rx="4"
                                    fill={
                                        isDark
                                            ? 'rgba(0,0,0,0.8)'
                                            : 'rgba(255,255,255,0.9)'
                                    }
                                />
                                <text
                                    x="47"
                                    y="213"
                                    textAnchor="middle"
                                    fontSize="8"
                                    fill={txtColor}
                                    fontWeight="bold"
                                >
                                    Agitador{' '}
                                    {temps?.magneticStirrer?.toFixed(1) ?? '—'}
                                    °C
                                </text>
                                <line
                                    x1="90"
                                    y1="210"
                                    x2="96.2"
                                    y2="210"
                                    stroke={txtColor}
                                    strokeWidth="1"
                                    strokeDasharray="2"
                                />

                                {/* Label Trocador de Calor apontando para a haste direita do U (Y=249) */}
                                <rect
                                    x="188"
                                    y="240"
                                    width="102"
                                    height="18"
                                    rx="4"
                                    fill={
                                        isDark
                                            ? 'rgba(0,0,0,0.8)'
                                            : 'rgba(255,255,255,0.9)'
                                    }
                                />
                                <text
                                    x="239"
                                    y="252"
                                    textAnchor="middle"
                                    fontSize="7.5"
                                    fill={txtColor}
                                    fontWeight="bold"
                                >
                                    Troc. Calor{' '}
                                    {temps?.sampleHeatExchanger?.toFixed(1) ??
                                        '—'}
                                    °C
                                </text>
                                <line
                                    x1="178.6"
                                    y1="249"
                                    x2="188"
                                    y2="249"
                                    stroke={txtColor}
                                    strokeWidth="1"
                                    strokeDasharray="2"
                                />
                            </g>
                        )}
                    </g>

                    {/* =========================================
                        TUBO DE ENSAIO
                        ========================================= */}
                    <g
                        opacity={z.tube.op > 0 || z.tubeGlass.op > 0 ? 1 : 0}
                        style={{ transition: 'all 0.3s ease' }}
                    >
                        {/* Corpo do Tubo */}
                        <path
                            fill="url(#dtb)"
                            d="M147.77017,223.81654h-0.00003c-10.32959,0-18.70335-8.37378-18.70335-18.70335V33.39036h37.40674v171.72282C166.47353,215.44276,158.09975,223.81654,147.77017,223.81654z"
                        />
                        <path
                            fill="url(#dgl)"
                            d="M147.77017,223.81654h-0.00003c-10.32959,0-18.70335-8.37378-18.70335-18.70335V33.39036h37.40674v171.72282C166.47353,215.44276,158.09975,223.81654,147.77017,223.81654z"
                        />
                        <path
                            fill="none"
                            stroke={
                                isDark
                                    ? 'rgba(255,255,255,0.4)'
                                    : 'rgba(0,0,0,0.3)'
                            }
                            strokeWidth="1.5"
                            d="M147.77017,223.81654h-0.00003c-10.32959,0-18.70335-8.37378-18.70335-18.70335V33.39036h37.40674v171.72282C166.47353,215.44276,158.09975,223.81654,147.77017,223.81654z"
                        />

                        <rect
                            x="127"
                            y="27"
                            width="41"
                            height="6"
                            rx="2"
                            fill={isDark ? '#555' : '#ccc'}
                            stroke="#999"
                            strokeWidth="1"
                        />

                        {/* Label Amostra */}
                        {(viewMode === 1 || viewMode === 0) && (
                            <g>
                                <rect
                                    x="195"
                                    y="110"
                                    width="95"
                                    height="18"
                                    rx="4"
                                    fill={
                                        isDark
                                            ? 'rgba(0,0,0,0.8)'
                                            : 'rgba(255,255,255,0.9)'
                                    }
                                />
                                <text
                                    x="242"
                                    y="122"
                                    textAnchor="middle"
                                    fontSize="8"
                                    fill={txtColor}
                                    fontWeight="bold"
                                >
                                    AMOSTRA {temps?.sample?.toFixed(1) ?? '—'}°C
                                </text>
                                <line
                                    x1="166.4"
                                    y1="119"
                                    x2="195"
                                    y2="119"
                                    stroke={txtColor}
                                    strokeWidth="1"
                                    strokeDasharray="2"
                                />
                            </g>
                        )}
                    </g>

                    {/* =========================================
                        DISSIPADORES (Heatsinks limpos)
                        ========================================= */}
                    <g
                        opacity={z.hs.op}
                        style={{ transition: 'all 0.3s ease' }}
                    >
                        <rect
                            x="67"
                            y="65"
                            width="160"
                            height="182"
                            fill={tc.hsUp}
                            stroke={isDark ? '#444' : '#999'}
                            strokeWidth="1.5"
                            rx="4"
                        />
                        <rect
                            x="67"
                            y="275"
                            width="160"
                            height="133"
                            fill={tc.hsLow}
                            stroke={isDark ? '#444' : '#999'}
                            strokeWidth="1.5"
                            rx="4"
                        />

                        {viewMode === 2 && isBusy && (
                            <g>
                                <rect
                                    x="5"
                                    y="110"
                                    width="55"
                                    height="22"
                                    rx="4"
                                    fill={
                                        isDark
                                            ? 'rgba(0,0,0,0.8)'
                                            : 'rgba(255,255,255,0.9)'
                                    }
                                />
                                <text
                                    x="32"
                                    y="120"
                                    textAnchor="middle"
                                    fontSize="7"
                                    fill={isDark ? '#aaa' : '#666'}
                                >
                                    Superior
                                </text>
                                <text
                                    x="32"
                                    y="129"
                                    textAnchor="middle"
                                    fontSize="9"
                                    fill={txtColor}
                                    fontWeight="bold"
                                >
                                    {temps?.heatsinkUpper?.toFixed(1) ?? '—'}°C
                                </text>

                                <rect
                                    x="5"
                                    y="335"
                                    width="55"
                                    height="22"
                                    rx="4"
                                    fill={
                                        isDark
                                            ? 'rgba(0,0,0,0.8)'
                                            : 'rgba(255,255,255,0.9)'
                                    }
                                />
                                <text
                                    x="32"
                                    y="345"
                                    textAnchor="middle"
                                    fontSize="7"
                                    fill={isDark ? '#aaa' : '#666'}
                                >
                                    Inferior
                                </text>
                                <text
                                    x="32"
                                    y="354"
                                    textAnchor="middle"
                                    fontSize="9"
                                    fill={txtColor}
                                    fontWeight="bold"
                                >
                                    {temps?.heatsinkLower?.toFixed(1) ?? '—'}°C
                                </text>
                            </g>
                        )}
                    </g>

                    {/* =========================================
                        SENSORES ÓPTICOS E FEIXES (Modo Espectro)
                        ========================================= */}
                    {isBusy &&
                        isOnline &&
                        viewMode === 0 &&
                        SENSOR_LEDS.map((s, i) => {
                            const active = sensorIdx === i;
                            return (
                                <g key={`sensor${i}`}>
                                    {active && (
                                        <>
                                            <line
                                                x1="110"
                                                y1={s.cy}
                                                x2="184"
                                                y2={s.cy}
                                                stroke={s.color}
                                                strokeWidth="3"
                                                strokeDasharray="5 3"
                                                opacity="0.6"
                                            />
                                            <line
                                                x1="110"
                                                y1={s.cy}
                                                x2="184"
                                                y2={s.cy}
                                                stroke="#ffffff"
                                                strokeWidth="1"
                                                strokeDasharray="5 3"
                                                opacity="0.9"
                                            />
                                        </>
                                    )}
                                    <rect
                                        x="120"
                                        y={s.cy - 6}
                                        width="6"
                                        height="12"
                                        rx="2"
                                        fill={
                                            active
                                                ? s.color
                                                : isDark
                                                  ? '#444'
                                                  : '#ccc'
                                        }
                                    />
                                    <rect
                                        x="168"
                                        y={s.cy - 6}
                                        width="6"
                                        height="12"
                                        rx="2"
                                        fill={isDark ? '#333' : '#bbb'}
                                    />
                                </g>
                            );
                        })}

                    {/* =========================================
                        BOTÕES SELETORES EXTERNOS (Modo Espectro)
                        ========================================= */}
                    {viewMode === 0 && isBusy && isOnline && (
                        <g>
                            {/* Linhas de Conexão Retas */}
                            {SENSOR_LEDS.map((s, i) => {
                                const active = sensorIdx === i;
                                if (!active) return null;
                                return (
                                    <path
                                        key={`conn${i}`}
                                        d={`M 44 ${s.cy} L 120 ${s.cy}`}
                                        fill="none"
                                        stroke={s.color}
                                        strokeWidth="1.5"
                                        strokeDasharray="3 3"
                                        opacity="0.6"
                                    />
                                );
                            })}

                            {/* Pílulas Interativas — Sensores Ópticos */}
                            {SENSOR_LEDS.map((s, i) => {
                                const active = sensorIdx === i;
                                return (
                                    <g
                                        key={`btn${i}`}
                                        onClick={() => setSensorIdx(i)}
                                        style={{
                                            cursor: 'pointer',
                                            transition: 'all 0.2s',
                                        }}
                                    >
                                        <rect
                                            x="2"
                                            y={s.btnY - 11}
                                            width="42"
                                            height="22"
                                            rx="6"
                                            fill={
                                                active
                                                    ? s.color
                                                    : isDark
                                                      ? '#2a2a2a'
                                                      : '#e0e0e0'
                                            }
                                            stroke={
                                                active
                                                    ? '#fff'
                                                    : isDark
                                                      ? '#444'
                                                      : '#bbb'
                                            }
                                            strokeWidth="1.5"
                                            filter={
                                                active
                                                    ? 'url(#shadow-led)'
                                                    : 'none'
                                            }
                                        />
                                        <text
                                            x="23"
                                            y={s.btnY + 3}
                                            textAnchor="middle"
                                            fontSize="9"
                                            fill={
                                                active
                                                    ? '#fff'
                                                    : isDark
                                                      ? '#aaa'
                                                      : '#666'
                                            }
                                            fontWeight="bold"
                                            fontFamily="sans-serif"
                                        >
                                            {s.shortName}
                                        </text>
                                    </g>
                                );
                            })}

                            {/* Pílula — Frequência Ressonante Ch0 */}
                            <g>
                                <rect
                                    x="2"
                                    y={FREQ_LED.btnY - 11}
                                    width="42"
                                    height="22"
                                    rx="6"
                                    fill={isDark ? '#2a2a2a' : '#e0e0e0'}
                                    stroke={FREQ_LED.color}
                                    strokeWidth="1.5"
                                />
                                <text
                                    x="23"
                                    y={FREQ_LED.btnY + 3}
                                    textAnchor="middle"
                                    fontSize="8"
                                    fill={FREQ_LED.color}
                                    fontWeight="bold"
                                    fontFamily="sans-serif"
                                >
                                    {FREQ_LED.shortName}
                                </text>
                                {/* Linha conectando ao tubo */}
                                <path
                                    d={`M 44 ${FREQ_LED.cy} L 120 ${FREQ_LED.cy}`}
                                    fill="none"
                                    stroke={FREQ_LED.color}
                                    strokeWidth="1"
                                    strokeDasharray="3 3"
                                    opacity="0.4"
                                />
                                {/* Ondinha no sensor esquerdo */}
                                <path
                                    d={`M 120 ${FREQ_LED.cy - 6} q 3,6 0,12`}
                                    fill="none"
                                    stroke={FREQ_LED.color}
                                    strokeWidth="1.5"
                                    opacity="0.8"
                                />
                                <path
                                    d={`M 124 ${FREQ_LED.cy - 6} q 3,6 0,12`}
                                    fill="none"
                                    stroke={FREQ_LED.color}
                                    strokeWidth="1"
                                    opacity="0.5"
                                />
                                {/* Label valor MHz à direita */}
                                <rect
                                    x="190"
                                    y={FREQ_LED.cy - 9}
                                    width="100"
                                    height="18"
                                    rx="4"
                                    fill={
                                        isDark
                                            ? 'rgba(0,0,0,0.8)'
                                            : 'rgba(255,255,255,0.9)'
                                    }
                                />
                                <text
                                    x="240"
                                    y={FREQ_LED.cy + 4}
                                    textAnchor="middle"
                                    fontSize="7.5"
                                    fill={FREQ_LED.color}
                                    fontWeight="bold"
                                >
                                    Freq. Ch0:{' '}
                                    {freq?.channel0 != null
                                        ? `${(freq.channel0 / 1e6).toFixed(3)} MHz`
                                        : '—'}
                                </text>
                            </g>
                        </g>
                    )}

                    {/* =========================================
                        CAMADA EXTERNA: CARCAÇA FLAT FRONTAL
                        ========================================= */}
                    <g
                        opacity={z.shellFront.op}
                        style={{
                            transition: 'opacity 0.3s ease',
                            pointerEvents: viewMode === 3 ? 'auto' : 'none',
                        }}
                    >
                        <rect
                            x="47"
                            y="60"
                            width="200"
                            height="380"
                            rx="4"
                            fill={isDark ? '#2a2a2a' : '#e5e7eb'}
                            stroke={isDark ? '#111' : '#d1d5db'}
                            strokeWidth="1.5"
                        />

                        {/* Saia preta — base da carcaça */}
                        <rect
                            x="47"
                            y="425"
                            width="200"
                            height="16"
                            rx="3"
                            fill="#111"
                            stroke="#000"
                            strokeWidth="0.5"
                        />

                        <g opacity="0.7">
                            {VENT_HOLES.map((hole, i) => (
                                <circle
                                    key={`vh${i}`}
                                    cx={hole.x}
                                    cy={hole.y}
                                    r={hole.r}
                                    fill={isDark ? '#000' : '#111'}
                                />
                            ))}
                            {/* Disco central — cor da carcaça (maciço, não é furo) */}
                            <circle
                                cx={VENT_CENTER.x}
                                cy={VENT_CENTER.y}
                                r={VENT_CORE_R}
                                fill={isDark ? '#2a2a2a' : '#e5e7eb'}
                                stroke={isDark ? '#444' : '#ccc'}
                                strokeWidth="0.5"
                            />
                        </g>

                        <rect
                            x="110"
                            y="85"
                            width="74"
                            height="185"
                            rx="12"
                            fill="#222"
                        />

                        {/* ÍCONE 1: Cloud (Material Symbols 960) */}
                        <g
                            transform="translate(147, 110)"
                            filter={
                                ledCloud !== LED_COLORS.off
                                    ? 'url(#shadow-led)'
                                    : 'none'
                            }
                        >
                            <g transform="scale(0.038) translate(-480, 480)">
                                <path
                                    d="M260-160q-91 0-155.5-63T40-377q0-78 47-139t123-78q25-92 100-149t170-57q117 0 198.5 81.5T760-520q69 8 114.5 59.5T920-340q0 75-52.5 127.5T740-160H260Zm0-80h480q42 0 71-29t29-71q0-42-29-71t-71-29h-60v-80q0-83-58.5-141.5T480-720q-83 0-141.5 58.5T280-520h-20q-58 0-99 41t-41 99q0 58 41 99t99 41Zm220-240Z"
                                    fill={ledCloud}
                                />
                            </g>
                        </g>

                        {/* ÍCONE 2: Microbiology (Material Symbols 960) */}
                        <g
                            transform="translate(147, 165)"
                            filter={
                                ledStatus !== LED_COLORS.off
                                    ? 'url(#shadow-led)'
                                    : 'none'
                            }
                        >
                            <g transform="scale(0.038) translate(-480, 480)">
                                <path
                                    d="M600-80q-17 0-28.5-11.5T560-120v-43q-23-4-43.5-11.5T478-195l-40 40q-12 11-28.5 11.5T381-155q-12-12-12-28.5t12-28.5l41-41q-3-5-6-10.5t-6-10.5l-27-53-49 49q-12 11-28 11.5T278-278q-12-12-12-28t12-28l49-50-53-26q-5-2-9-4.5t-9-5.5l-36 36q-12 11-28.5 11.5T163-384q-12-12-12-28t12-28l35-35q-14-19-22.5-40.5T163-560h-43q-17 0-28.5-11.5T80-600q0-17 11.5-28.5T120-640h45q5-19 12-36t18-33l-35-35q-12-12-12-28t12-28q12-12 28-12t28 12l35 35q16-11 33-18t36-12v-45q0-17 11.5-28.5T360-880q17 0 28.5 11.5T400-840v43q24 4 45.5 13t40.5 23l35-35q12-12 28.5-12t28.5 12q12 12 12 28t-12 28l-37 37q2 4 4.5 8t4.5 9l25 50 46-46q12-12 28.5-12t28.5 12q12 12 12 28.5T678-625l-48 47 56 28q6 3 12.5 6.5T710-536l40-40q12-12 28-12t28 12q12 12 12 28.5T806-519l-40 39q12 18 19.5 38t11.5 42h43q17 0 28.5 11.5T880-360q0 17-11.5 28.5T840-320h-45q-5 19-12 35.5T765-252l34 34q12 12 12 28.5T799-161q-12 11-28.5 11.5T742-161l-33-34q-16 11-33 18t-36 12v45q0 17-11.5 28.5T600-80Zm-6-160q58 0 95.5-44T718-386q-5-30-22.5-54T650-478l-66-34q-23-12-41.5-30.5T512-584l-34-66q-16-32-46-51t-66-19q-58 0-95.5 44T242-574q5 30 22.5 54t45.5 38l66 34q23 12 41.5 30.5T448-376l34 66q16 32 46 51t66 19ZM380-540q25 0 42.5-17.5T440-600q0-25-17.5-42.5T380-660q-25 0-42.5 17.5T320-600q0 25 17.5 42.5T380-540Zm235.5 235.5Q630-319 630-340t-14.5-35.5Q601-390 580-390t-35.5 14.5Q530-361 530-340t14.5 35.5Q559-290 580-290t35.5-14.5ZM480-480Z"
                                    fill={ledStatus}
                                />
                            </g>
                        </g>

                        {/* ÍCONE 3: Power Settings New (Material Symbols 960) */}
                        <g
                            transform="translate(147, 220)"
                            filter={
                                ledPower !== LED_COLORS.off
                                    ? 'url(#shadow-led)'
                                    : 'none'
                            }
                        >
                            <g transform="scale(0.038) translate(-480, 480)">
                                <path
                                    d="M480-80q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-84 31.5-156.5T197-763l56 56q-44 44-68.5 102T160-480q0 134 93 227t227 93q134 0 227-93t93-227q0-67-24.5-125T707-707l56-56q54 54 85.5 126.5T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Zm-40-360v-440h80v440h-80Z"
                                    fill={ledPower}
                                />
                            </g>
                        </g>

                        <text
                            x="147"
                            y="258"
                            textAnchor="middle"
                            fontSize="11"
                            fill="#ccc"
                            fontWeight="bold"
                            fontFamily="sans-serif"
                        >
                            BioAiLab
                        </text>

                        {/* LABEL AMBIENTE */}
                        {viewMode === 3 && (
                            <g>
                                <rect
                                    x="230"
                                    y="200"
                                    width="60"
                                    height="22"
                                    rx="4"
                                    fill={
                                        isDark
                                            ? 'rgba(0,0,0,0.7)'
                                            : 'rgba(255,255,255,0.9)'
                                    }
                                />
                                <text
                                    x="260"
                                    y="210"
                                    textAnchor="middle"
                                    fontSize="7"
                                    fill={isDark ? '#aaa' : '#666'}
                                    fontWeight="bold"
                                >
                                    AMBIENTE
                                </text>
                                <text
                                    x="260"
                                    y="219"
                                    textAnchor="middle"
                                    fontSize="9"
                                    fill={txtColor}
                                    fontWeight="bold"
                                >
                                    {temps?.ambient?.toFixed(1) ?? '—'}°C
                                </text>
                            </g>
                        )}
                    </g>

                    {/* Logo Superior µTOWER */}
                    <text
                        x="147"
                        y="-10"
                        textAnchor="middle"
                        fontSize="14"
                        fontFamily="sans-serif"
                        fill={txtColor}
                        fontWeight="900"
                        letterSpacing="4"
                        opacity="0.8"
                    >
                        BioAiLab
                    </text>
                </svg>
            </Box>
        </Box>
    );
}
